import { ArticleData } from './ArticleResource';
import BaseResource from './BaseResource';
import { StudentData } from './StudentResource';
import { TutorData } from './TutorResource';

interface ChatLine {
  text: string;
  isUser: boolean;
  meta: any;
  audio: string;
}

export interface FeedbackMetricValue {
  raw?: number;
  level?: {
    value: number;
    description: {
      en: string;
      ja: string;
    };
  };
}

interface FeedbackMetric {
  key: string;
  value: string | FeedbackMetricValue;
}

export interface FeedbackData {
  chatId: string;
  content: string;
  contentJp: string;
  type: string;
  metrics: FeedbackMetric[];
  section: string;
  createdAt: string;
}

export interface ChatData {
  id: string;
  image: string;
  lines: ChatLine[];
  missions: any[];
  tutor: TutorData;
  difficulty: string;
  news: ArticleData;
  duration: number;
  createdAt: string;
  type: string;
  meta: any;
  feedbacks: FeedbackData[];
  userId: string;
  student: StudentData;
}

export interface ParsedScore {
  label: string;
  percentage: string;
  score: string;
  description: string;
  descriptionJp: string;
  type?: string;
  maxScore?: number;
  unit?: string;
  name: string;
}

export interface SectionScore {
  title: string;
  maxScore: number;
  passScore: number;
  score: string | number | undefined;
}

const defaultConfigMap = {
  arousal: {
    name: 'arousal',
    label: 'Energy',
    unit: '%',
  },
  speechPace: {
    name: 'speechPace',
    label: 'Speech Pace',
    unit: 'wpm',
  },
  valence: {
    name: 'valence',
    label: 'Attitude',
    unit: '%',
  },
  eyeContact: {
    name: 'eyeContact',
    label: 'Eye Contact',
    unit: '%',
  },
  clarity: {
    name: 'clarity',
    label: 'Clarity',
    unit: '%',
  },
  pronunciation: {
    name: 'pronunciation',
    label: 'Pronunciation',
    unit: '%',
  },
};

const calculatePassThreshold = (maxScore?: number) => {
  if (maxScore === 3) {
    return 2;
  } else if (maxScore === 5) {
    return 4;
  } else if (maxScore === 10) {
    return 8;
  } else if (maxScore === 33) {
    return 23;
  }

  return 0;
};

class ChatResource extends BaseResource<ChatData> {
  static get storeName(): string {
    return 'ChatResource';
  }

  static get endpoint(): string {
    return `/${this.scope}/chats`;
  }

  static async fetchLatestChatsForUser(
    userId: string,
    type?: string
  ): Promise<ChatResource[]> {
    let url = `/${this.scope}/chats?userId=${userId}&limit=4&includeOthers=true&type=eiken2`;
    if (type) url += `&eikenTestType=${type}`;
    const raw = await this.http.get(url);
    return this.upsertManyAndReturn(raw);
  }

  static listLatestChatsForUser(userId: string, type?: string): ChatResource[] {
    const chats = this.all().filter(
      (chat) =>
        chat.data.userId === userId &&
        (!type || chat.data.meta?.eikenTestType === type)
    );
    return chats
      .sort((a, b) => b.data.createdAt - a.data.createdAt)
      .slice(0, 4);
  }

  static toObject(raw: any): ChatResource {
    return new ChatResource(raw as ChatData);
  }

  static parseScores(
    names: string[],
    chat: ChatData,
    section: string,
    maxScore?: number
  ): ParsedScore[] {
    let feedbacks = chat.feedbacks.filter((feedback) => feedback.section === section);

    // If there are more than one feedback of the same type for one section, only use the latest one for parsing scores
    const reversedFeedbacks = [...feedbacks].reverse();
    feedbacks = reversedFeedbacks.filter(
      (feedback, index) => index === reversedFeedbacks.findIndex((t) => t.type === feedback.type)
    );
    const metrics = feedbacks.flatMap((feedback) => feedback.metrics);
    const scores = metrics.filter((metric) => names.includes(metric.key));

    const parsedScores = scores.map((score) => {
      const parsed = score.value as FeedbackMetricValue;

      // Clarity and Eye Contact for fulltest are not scored
      let computedMaxScore = maxScore;
      if (
        score.key === 'clarity' ||
        (section !== 'fulltest' && score.key === 'eyeContact')
      ) {
        computedMaxScore = undefined;
      }

      if (typeof parsed.raw !== 'number' || parsed.raw < 0) {
        return {
          ...defaultConfigMap[score.key as keyof typeof defaultConfigMap],
          percentage: '0',
          score: '-',
          description: 'No data available',
          descriptionJp: 'データがありません',
          maxScore: computedMaxScore,
        };
      }

      if (!parsed.level) {
        return {
          ...defaultConfigMap[score.key as keyof typeof defaultConfigMap],
          percentage: parsed.raw?.toFixed(0) || '0',
          score: '-',
          description: 'No data available',
          descriptionJp: 'データがありません',
          maxScore: computedMaxScore,
        };
      }

      return {
        ...defaultConfigMap[score.key as keyof typeof defaultConfigMap],
        percentage: parsed.raw?.toFixed(0) || '0',
        score: parsed.level?.value.toFixed(0) || '-',
        type:
          parsed.level.value >= calculatePassThreshold(computedMaxScore)
            ? 'pass'
            : 'fail',
        description: parsed.level.description.en,
        descriptionJp: parsed.level.description.ja,
        maxScore: computedMaxScore,
      };
    });

    // Order parsedScores by the order of names
    return parsedScores.sort(
      (a, b) => names.indexOf(a.name) - names.indexOf(b.name)
    );
  }

  // Todo: merge this with parseScores
  static getScoreForType(chat: ChatData, type: string): SectionScore {
    type = type || chat.meta?.eikenTestType;

    switch (type) {
      case 'fulltest':
        return {
          title: 'Full test',
          maxScore: 33,
          passScore: 23,
          score: chat.feedbacks
            .find(
              (feedback) =>
                feedback.type === 'overallFeedback' &&
                feedback.section === 'fulltest'
            )
            ?.metrics.find((metric) => metric.key === 'totalScore')
            ?.value as string,
        };

      case 'pronunciation':
        const pronunciationScore =
          (
            chat.feedbacks
              .find(
                (feedback) =>
                  feedback.type === 'speechFeedback' &&
                  feedback.section === 'pronunciation'
              )
              ?.metrics.find((metric) => metric.key === 'pronunciation')
              ?.value as FeedbackMetricValue
          )?.level?.value || 0;

        const arousalScore =
          (
            chat.feedbacks
              .find(
                (feedback) =>
                  feedback.type === 'arousalValenceFeedback' &&
                  feedback.section === 'pronunciation'
              )
              ?.metrics.find((metric) => metric.key === 'arousal')
              ?.value as FeedbackMetricValue
          )?.level?.value || 0;

        const valenceScore =
          (
            chat.feedbacks
              .find(
                (feedback) =>
                  feedback.type === 'arousalValenceFeedback' &&
                  feedback.section === 'pronunciation'
              )
              ?.metrics.find((metric) => metric.key === 'valence')
              ?.value as FeedbackMetricValue
          )?.level?.value || 0;

        const speechPaceScore =
          (
            chat.feedbacks
              .find(
                (feedback) =>
                  feedback.type === 'speechFeedback' &&
                  feedback.section === 'pronunciation'
              )
              ?.metrics.find((metric) => metric.key === 'speechPace')
              ?.value as FeedbackMetricValue
          )?.level?.value || 0;

        const scores = [
          pronunciationScore,
          arousalScore,
          valenceScore,
          speechPaceScore,
        ];

        const finalScore = scores.reduce((min, item) => {
          if (item <= 0) {
            return min;
          }

          return Math.min(min, Number(item));
        }, Infinity);

        return {
          title: 'Pronunciation',
          maxScore: 5,
          passScore: 4,
          score: finalScore === Infinity ? undefined : finalScore.toFixed(0),
        };

      case 'q1':
        return {
          title: 'Q1',
          maxScore: 5,
          passScore: 4,
          score: chat.feedbacks
            .find(
              (feedback) =>
                feedback.type === 'summary' && feedback.section === type
            )
            ?.metrics.find((metric) => metric.key === 'score')?.value as string,
        };

      case 'q2':
        return {
          title: 'Q2',
          maxScore: 10,
          passScore: 8,
          score: chat.feedbacks
            .find(
              (feedback) =>
                feedback.type === 'summary' && feedback.section === type
            )
            ?.metrics.find((metric) => metric.key === 'score')?.value as string,
        };

      case 'q3':
        return {
          title: 'Q3',
          maxScore: 5,
          passScore: 4,
          score: chat.feedbacks
            .find(
              (feedback) =>
                feedback.type === 'summary' && feedback.section === type
            )
            ?.metrics.find((metric) => metric.key === 'score')?.value as string,
        };

      case 'q4':
        return {
          title: 'Q4',
          maxScore: 5,
          passScore: 4,
          score: chat.feedbacks
            .find(
              (feedback) =>
                feedback.type === 'summary' && feedback.section === type
            )
            ?.metrics.find((metric) => metric.key === 'score')?.value as string,
        };
      default:
        return {
          title: 'N/A',
          maxScore: 0,
          passScore: 0,
          score: '-',
        };
    }
  }
}

export default ChatResource;
